/**
 * SharkForge — turn a player's spec into a shark the game can actually draw.
 *
 * The generator emits a voxel CLOUD: twenty to thirty thousand cubes with a continuous colour
 * per cube. Drawn as instanced boxes that is a quarter of a million triangles for one animal,
 * which is fine for a turntable and impossible for a reef with five players in it. So it goes
 * through the same greedy mesher everything else uses, and comes out an order of magnitude
 * cheaper for an identical silhouette.
 *
 * WHICH FORCES THE ONE INTERESTING DECISION IN THIS FILE.
 * Greedy meshing merges on colour EQUALITY. The generator hands over 940 to 2,350 distinct
 * colours per shark — a countershading gradient, markings, per-voxel grain — so meshing it
 * raw would merge almost nothing and save almost nothing. The colours have to be quantised to
 * a palette first, and the quantisation is therefore not a compression step, it IS the
 * rendering budget.
 *
 * Uniform quantisation in DISPLAY space rather than clustering: display space is already
 * roughly perceptual, so even steps there are even steps to the eye, and the banding lands
 * along the gradient instead of wherever a k-means happened to converge. It is also stable —
 * two sharks a seed apart get the same bands — which a clustered palette would not be.
 *
 * The level count backs off until the palette fits, because `VoxelVolume.data` is a Uint8Array
 * and a slot index has to fit in a byte.
 *
 * NOTHING HERE TOUCHES THREE, and that is load-bearing rather than tidy. Building one shark
 * takes 300–600 ms, which is five to thirty dropped frames — unusable behind a slider. Keeping
 * this file to plain arrays lets the mesh worker import it, so a player dragging a size dial
 * gets a rebuild off-thread and the page never stutters. The THREE side lives in MeshService.
 */
import { greedyMesh } from './GreedyMesh.ts';
import type { VoxelVolume } from './Sculpt.ts';
import { buildShark, SPECIES, shareCode } from '../world/shark/voxelShark.js';

/** Fixed, deliberately. Every shark shares one voxel pitch so they share one look. */
export const GRID = 200;

/**
 * World units per metre of real animal.
 *
 * Calibrated against the roster the game already had: a shark landed at ~8.1 u and reads as
 * about four metres. Everything else in the world — the reef, the kelp, the turtles — is
 * scaled to that, so this is the number that keeps a player's shark in the same ocean.
 */
export const UNITS_PER_METRE = 1.95;

/** starting point for the palette; drops until the slot indices fit in a byte */
const START_LEVELS = 7;

export interface SharkSpec {
  /** key into the species table */
  species: string;
  seed: number;
  /** 0.6 .. 1.4 multiplier on the species' natural length */
  size: number;
  /** 0 juvenile (strong markings) .. 1 adult (faded) */
  age: number;
  /** whatever the player typed. Never generated — a shark nobody named is nobody's shark. */
  name: string;
}

/** the jaw hinge and rostrum pivots, in the mesh's own recentred space */
export interface ForgedJaw {
  hingeLowerZ: number;
  hingeLowerY: number;
  hingeUpperZ: number;
  hingeUpperY: number;
  snoutEndZ: number;
  shakeZ: number;
}

export interface ForgedVolume {
  vol: VoxelVolume;
  palette: Float32Array;
  /** world units per voxel */
  voxelSize: number;
  jaw: ForgedJaw;
  /** nose-to-tail, world units */
  length: number;
  /** the species actually used, after an unknown key falls back */
  species: string;
  code: string;
}

/** the roster a chooser shows: key, display name, latin, natural length */
export function speciesList(): Array<{ key: string; name: string; latin: string; metres: number }> {
  return Object.keys(SPECIES).map((k) => ({
    key: k,
    name: SPECIES[k].name,
    latin: SPECIES[k].latin,
    metres: SPECIES[k].metres,
  }));
}

/**
 * Species keys, in a FIXED order.
 *
 * The wire carries an index into this, not the string: the pose channel is twelve packets a
 * second per shark and a species is one byte. Append only — inserting a key renames every
 * shark already shared by link, because a peer decodes the index against its own copy of this
 * list and would build somebody else's animal.
 */
export const SPECIES_ORDER: string[] = Object.keys(SPECIES);

export function speciesIndex(key: string): number {
  const i = SPECIES_ORDER.indexOf(key);
  return i < 0 ? 0 : i;
}

export function speciesKey(index: number): string {
  const n = SPECIES_ORDER.length;
  return SPECIES_ORDER[((index % n) + n) % n];
}

export function defaultSpec(seed = 1337): SharkSpec {
  return { species: 'tiger', seed: seed >>> 0, size: 1, age: 0.2, name: '' };
}

const toLinear = (c: number): number =>
  c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

/**
 * Spec in, voxel volume out. No THREE, no geometry — see the note at the top of the file.
 */
export function forgeVolume(spec: SharkSpec): ForgedVolume {
  const key = SPECIES[spec.species] ? spec.species : 'tiger';
  const sp = SPECIES[key];
  const D = buildShark(key, GRID, spec.seed >>> 0, spec.age);

  /* ---- quantise ------------------------------------------------------------------------
     Back the level count off until the distinct colours fit in a byte. Seven levels a channel
     is 343 possible combinations but far fewer are ever used, so this almost always lands
     first time and only the busiest markings push it down. */
  let levels = START_LEVELS;
  let lut = new Map<number, number>();
  let slots: number[] = [];
  for (;;) {
    lut = new Map();
    slots = [];
    const step = levels - 1;
    for (let i = 0; i < D.n; i++) {
      const r = Math.round(D.c[i * 3] * step);
      const g = Math.round(D.c[i * 3 + 1] * step);
      const b = Math.round(D.c[i * 3 + 2] * step);
      const k = (r * levels + g) * levels + b;
      let s = lut.get(k);
      if (s === undefined) {
        s = lut.size;
        lut.set(k, s);
        slots.push(k);
      }
    }
    if (lut.size <= 254 || levels <= 3) break;
    levels--;
  }

  const palette = new Float32Array(Math.max(1, lut.size) * 3);
  for (let s = 0; s < slots.length; s++) {
    const k = slots[s];
    const step = levels - 1;
    const b = k % levels, g = ((k - b) / levels) % levels, r = Math.floor(k / (levels * levels));
    // Display space in, LINEAR out: the mesher's palette and this whole renderer are linear.
    palette[s * 3] = toLinear(r / step);
    palette[s * 3 + 1] = toLinear(g / step);
    palette[s * 3 + 2] = toLinear(b / step);
  }

  /* ---- pack into the mesher's volume ---------------------------------------------------
     Axis remap. The generator runs the body along +x with the nose at 0; the mesher's contract
     is [w, h, l] = X lateral, Y up, Z spine, with the NOSE at high Z and the belly at y = 0.
     So the spine axis is reversed as well as swapped, and getting either wrong builds a shark
     that swims backwards with its spineT gradient inside out. */
  const v = D.vox;
  const [X0, X1, Y0, Y1, Z0, Z1] = D.bounds;
  const NX = Math.round((X1 - X0) / v);
  const NY = Math.round((Y1 - Y0) / v);
  const NZ = Math.round((Z1 - Z0) / v);

  const dims: [number, number, number] = [NZ, NY, NX];
  const data = new Uint8Array(NZ * NY * NX);
  const groups = new Uint8Array(data.length);
  let minX = NZ, minY = NY, minZ = NX, maxX = -1, maxY = -1, maxZ = -1, solid = 0;

  const step = levels - 1;
  for (let i = 0; i < D.n; i++) {
    const gi = Math.round((D.x[i] - X0) / v - 0.5);
    const gj = Math.round((D.y[i] - Y0) / v - 0.5);
    const gk = Math.round((D.z[i] - Z0) / v - 0.5);
    if (gi < 0 || gi >= NX || gj < 0 || gj >= NY || gk < 0 || gk >= NZ) continue;

    const mx = gk;                 // lateral
    const my = gj;                 // up
    const mz = NX - 1 - gi;        // spine, nose at high Z
    const idx = mx + NZ * (my + NY * mz);

    const r = Math.round(D.c[i * 3] * step);
    const g = Math.round(D.c[i * 3 + 1] * step);
    const b = Math.round(D.c[i * 3 + 2] * step);
    const s = lut.get((r * levels + g) * levels + b) ?? 0;

    if (data[idx] === 0) solid++;
    data[idx] = s + 1;
    groups[idx] = D.g[i];
    if (mx < minX) minX = mx; if (mx > maxX) maxX = mx;
    if (my < minY) minY = my; if (my > maxY) maxY = my;
    if (mz < minZ) minZ = mz; if (mz > maxZ) maxZ = mz;
  }

  const vol: VoxelVolume & { group?: Uint8Array } = {
    dims, data, solidCount: solid,
    min: [minX, minY, minZ], max: [maxX, maxY, maxZ],
    group: groups,
  };

  // Length is set by the species and the player's size dial, not by the grid.
  const worldLength = sp.metres * spec.size * UNITS_PER_METRE;
  const voxelSize = worldLength / NX;

  /* The jaw rig, converted from the generator's model space into the mesh's recentred space.
     GreedyMesh re-centres on the solid AABB, so every pivot has to move with it or the
     mandible swings about a point somewhere outside the animal. */
  const cz = (minZ + maxZ + 1) * 0.5;
  const cy = (minY + maxY + 1) * 0.5;
  const modelToGridZ = (mx0: number): number => NX - (mx0 - X0) / v;
  const gz = (mx0: number): number => (modelToGridZ(mx0) - cz) * voxelSize;
  const gy = (my0: number): number => ((my0 - Y0) / v - cy) * voxelSize;

  return {
    vol,
    palette,
    voxelSize,
    jaw: {
      hingeLowerZ: gz(D.hingeX), hingeLowerY: gy(D.hingeY),
      hingeUpperZ: gz(D.snoutX), hingeUpperY: gy(D.snoutY),
      snoutEndZ: gz(D.snoutX),
      shakeZ: gz(Math.min(0.46, D.hingeX * 2.4)),
    },
    length: worldLength,
    species: key,
    code: shareCode(key, spec.seed >>> 0),
  };
}

/** The whole pipeline, for callers that already have a thread to spare (tests, the worker). */
export function forgeMesh(spec: SharkSpec): ReturnType<typeof greedyMesh> & { forged: ForgedVolume } {
  const forged = forgeVolume(spec);
  const out = greedyMesh(
    forged.vol, forged.palette, forged.voxelSize, 8192,
    (forged.vol as VoxelVolume & { group?: Uint8Array }).group,
  );
  return { ...out, forged };
}
